import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import DashboardLayout from '../../layouts/DashboardLayout';
import { RoleType, ScheduleType } from '../../types';
import { Location } from '../../types/location';
import { LocationService } from '../../services/location';
import { formatDate, getWeekDates } from '../../utils/dateUtils';

interface Employee {
  name: string;
  role: RoleType;
  hourlyRate: number;
  startTime: string;
  endTime: string;
}

const defaultEmployee: Employee = {
  name: '',
  role: 'Security Officer',
  hourlyRate: 15,
  startTime: '09:00',
  endTime: '17:00',
};

const ROLES: RoleType[] = ['Security Officer', 'Supervisor', 'Manager'];

const getRoleColor = (role: RoleType): string => {
  switch (role) {
    case 'Manager':
      return 'bg-blue-100';
    case 'Supervisor':
      return 'bg-green-100';
    case 'Security Officer':
      return 'bg-yellow-100';
    default:
      return 'bg-gray-100';
  }
};

const SchedulePage: React.FC = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [weekDates, setWeekDates] = useState<Date[]>(() => getWeekDates(new Date()));
  const [selectedLocationName, setSelectedLocationName] = useState<string>(() => {
    const saved = localStorage.getItem('selectedLocation');
    return saved || '';
  });
  const [availableLocations, setAvailableLocations] = useState<Location[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleType[]>([]);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [dragTimeout, setDragTimeout] = useState<NodeJS.Timeout | null>(null);
  const [newEmployee, setNewEmployee] = useState<Employee>(defaultEmployee);
  const [selectedEmployee, setSelectedEmployee] = useState<ScheduleType | null>(null);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        const locations = await LocationService.getAllLocations();
        setAvailableLocations(locations);
        if (!selectedLocationName && locations.length > 0) {
          setSelectedLocationName(locations[0].name);
          localStorage.setItem('selectedLocation', locations[0].name);
        }
      } catch (error) {
        console.error('Failed to load locations:', error);
      }
    };

    const handleLocationsUpdated = () => {
      loadLocations();
    };

    loadLocations();
    window.addEventListener('locationsUpdated', handleLocationsUpdated);

    return () => {
      window.removeEventListener('locationsUpdated', handleLocationsUpdated);
    };
  }, []);

  useEffect(() => {
    setWeekDates(getWeekDates(currentDate));
  }, [currentDate]);

  useEffect(() => {
    const savedSchedule = localStorage.getItem('scheduleData');
    if (savedSchedule) {
      try {
        const parsedSchedule = JSON.parse(savedSchedule);
        setScheduleData(parsedSchedule);
      } catch (error) {
        console.error('Error parsing schedule data:', error);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('scheduleData', JSON.stringify(scheduleData));
  }, [scheduleData]);

  useEffect(() => {
    return () => {
      if (dragTimeout) {
        clearTimeout(dragTimeout);
      }
    };
  }, [dragTimeout]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleLocationChange = (locationName: string) => {
    setSelectedLocationName(locationName);
    localStorage.setItem('selectedLocation', locationName);
  };

  const handleAddEmployee = () => {
    const newId = uuidv4();
    const newScheduleItem: ScheduleType = {
      id: newId,
      name: newEmployee.name,
      role: newEmployee.role,
      location: selectedLocationName,
      hourlyRate: newEmployee.hourlyRate,
      date: currentDate.toISOString(),
      startTime: newEmployee.startTime,
      endTime: newEmployee.endTime,
    };

    setScheduleData((prevData: ScheduleType[]) => {
      const updatedData = [...prevData, newScheduleItem];
      localStorage.setItem('scheduleData', JSON.stringify(updatedData));
      return updatedData;
    });

    setNewEmployee(defaultEmployee);
    setShowAddEmployeeModal(false);
  };

  const handleDragEnd = (result: DropResult): void => {
    if (!result.destination) return;

    const { source, destination } = result;
    const updatedItem = {
      ...scheduleData[source.index],
      date: weekDates[parseInt(destination.droppableId)].toISOString(),
    };

    setScheduleData((prevData: ScheduleType[]) => {
      const newSchedule = [...prevData];
      const draggedItem = newSchedule[source.index];
      if (!draggedItem) return prevData;

      newSchedule.splice(source.index, 1);
      newSchedule.splice(destination.index, 0, updatedItem);
      localStorage.setItem('scheduleData', JSON.stringify(newSchedule));
      return newSchedule;
    });
  };

  const handleDragStart = (): void => {
    if (dragTimeout) {
      clearTimeout(dragTimeout);
    }
  };

  const handleDeleteEmployee = (employeeId: string): void => {
    setScheduleData((prevData: ScheduleType[]) => {
      const updatedData = prevData.filter((item: ScheduleType) => item.id !== employeeId);
      localStorage.setItem('scheduleData', JSON.stringify(updatedData));
      return updatedData;
    });
    setShowEmployeeDetails(false);
  };

  const navigateWeek = (direction: 'prev' | 'next'): void => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentDate(newDate);
  };

  return (
    <DashboardLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-4 items-center">
            <h1 className="text-2xl font-bold">Schedule</h1>
            <select
              value={selectedLocationName}
              onChange={(e) => handleLocationChange(e.target.value)}
              className="border p-2 rounded"
            >
              <option value="">Select Location</option>
              {availableLocations.map((location) => (
                <option key={location._id} value={location.name}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowAddEmployeeModal(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Add Employee
            </button>
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Logout
            </button>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4">
          <button
            onClick={() => navigateWeek('prev')}
            className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
          >
            Previous Week
          </button>
          <span className="text-lg font-semibold">
            Week of {weekDates.length > 0 ? formatDate(weekDates[0]) : ''}
          </span>
          <button
            onClick={() => navigateWeek('next')}
            className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
          >
            Next Week
          </button>
        </div>

        <DragDropContext onDragEnd={handleDragEnd} onDragStart={handleDragStart}>
          <div className="grid grid-cols-7 gap-4">
            {weekDates.map((date: Date, index: number) => (
              <Droppable key={date.toISOString()} droppableId={index.toString()}>
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="border rounded p-4"
                  >
                    <h3 className="font-semibold mb-2">{formatDate(date)}</h3>
                    {scheduleData
                      .filter(
                        (item) =>
                          new Date(item.date).toDateString() === date.toDateString()
                      )
                      .map((item: ScheduleType, itemIndex: number) => (
                        <Draggable
                          key={item.id}
                          draggableId={item.id}
                          index={itemIndex}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`${getRoleColor(
                                item.role
                              )} p-2 mb-2 rounded cursor-pointer`}
                              onClick={() => {
                                setSelectedEmployee(item);
                                setShowEmployeeDetails(true);
                              }}
                            >
                              <div className="font-semibold">{item.name}</div>
                              <div className="text-sm">{item.role}</div>
                              <div className="text-sm">
                                {item.startTime} - {item.endTime}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            ))}
          </div>
        </DragDropContext>

        {showEmployeeDetails && selectedEmployee && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-96">
              <h2 className="text-xl font-bold mb-4">Employee Details</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <div className="text-lg">{selectedEmployee.name}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <div className="text-lg">{selectedEmployee.role}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Hourly Rate
                  </label>
                  <div className="text-lg">${selectedEmployee.hourlyRate}/hr</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Shift Time
                  </label>
                  <div className="text-lg">
                    {selectedEmployee.startTime} - {selectedEmployee.endTime}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowEmployeeDetails(false)}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Close
                </button>
                <button
                  onClick={() => handleDeleteEmployee(selectedEmployee.id)}
                  className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showAddEmployeeModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-white p-6 rounded-lg w-96">
              <h2 className="text-xl font-bold mb-4">Add Employee</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newEmployee.name}
                    onChange={(e) =>
                      setNewEmployee({ ...newEmployee, name: e.target.value })
                    }
                    className="w-full p-2 border rounded"
                    placeholder="Employee Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Role
                  </label>
                  <select
                    value={newEmployee.role}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        role: e.target.value as RoleType,
                      })
                    }
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select Role</option>
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Hourly Rate
                  </label>
                  <input
                    type="number"
                    value={newEmployee.hourlyRate}
                    onChange={(e) =>
                      setNewEmployee({
                        ...newEmployee,
                        hourlyRate: parseFloat(e.target.value),
                      })
                    }
                    className="w-full p-2 border rounded"
                    placeholder="Hourly Rate"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Shift Time
                  </label>
                  <div className="flex space-x-4">
                    <input
                      type="time"
                      value={newEmployee.startTime}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          startTime: e.target.value,
                        })
                      }
                      className="w-1/2 p-2 border rounded"
                    />
                    <input
                      type="time"
                      value={newEmployee.endTime}
                      onChange={(e) =>
                        setNewEmployee({
                          ...newEmployee,
                          endTime: e.target.value,
                        })
                      }
                      className="w-1/2 p-2 border rounded"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowAddEmployeeModal(false)}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddEmployee}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SchedulePage;
